import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { toIsoDateString, toDate } from './shiftDate';

// Contract:
// - Input: tenantId, punch { employeeId, timestamp, locationId, roleId }
// - Output: array of shift docs [{id, ...data}] that match:
//   - same employee
//   - scheduled on punch day or previous day (overnight)
//   - optional locationId filter when present
//   - optional roleId filter when present
//   - punch timestamp within a time window around shift start/end
// Notes:
// - This is used to decide auto vs user selection vs manual.
export default async function findValidShiftsForPunch(tenantId, punch, opts = {}) {
  const windowMinutes = typeof opts.windowMinutes === 'number' ? opts.windowMinutes : 60;
  if (!tenantId || !punch?.employeeId || !punch?.timestamp) return [];

  const tsDate = punch.timestamp?.toDate ? punch.timestamp.toDate() : new Date(punch.timestamp);
  const dayKey = punch?.date && /^\d{4}-\d{2}-\d{2}$/.test(String(punch.date))
    ? punch.date
    : toIsoDateString(tsDate);

  const dayStart = new Date(dayKey + 'T00:00:00'); // Local date, not UTC
  const nextDayStart = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  const prevDayStart = new Date(dayStart.getTime() - 24 * 60 * 60 * 1000);

  const inWindow = (shift) => {
    const parseShiftDateTime = (timeStr) => {
      if (!timeStr) return null;
      const [hh, mm] = String(timeStr).split(':').map(Number);
      if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
      const shiftDate = shift.shiftDateKey ? toDate(shift.shiftDateKey) : toDate(shift.shiftDate);
      const d = new Date(shiftDate);
      d.setHours(hh, mm, 0, 0);
      return d;
    };

    const start = parseShiftDateTime(shift.shiftStartTime || shift.startTime || shift.shiftStart || shift.start);
    const end = parseShiftDateTime(shift.shiftEndTime || shift.endTime || shift.shiftEnd || shift.end);
    if (!start || !end) return false;

    if (end.getTime() <= start.getTime()) {
      end.setDate(end.getDate() + 1);
    }

    const win = windowMinutes * 60 * 1000;
    const t = tsDate.getTime();
    return t >= start.getTime() - win && t <= end.getTime() + win;
  };

  const maybeFilter = (s) => {
    if (s.deleted) return false;
    if (punch.locationId && s.locationId && punch.locationId !== s.locationId) return false;
    if (punch.roleId && s.roleId && punch.roleId !== s.roleId) return false;
    return inWindow(s);
  };

  const fetchDay = opts.fetchDay || (async (startKey, endKey) => {
    const q = query(
      collection(db, `tenants/${tenantId}/shifts`),
      where('employeeId', '==', punch.employeeId),
      where('shiftDateKey', '>=', startKey),
      where('shiftDateKey', '<', endKey)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  });

  try {
    console.log('🔎 findValidShiftsForPunch called with:', {
      tenantId,
      employeeId: punch.employeeId,
      timestamp: punch.timestamp,
      locationId: punch.locationId,
      roleId: punch.roleId,
      dayKey,
      windowMinutes
    });
    const candidates = [];
    console.log('📅 Fetching current day shifts:', dayKey, 'to', toIsoDateString(nextDayStart));
    console.log('📅 Fetching current day shifts:', dayKey, 'to', toIsoDateString(nextDayStart));
    candidates.push(...await fetchDay(dayKey, toIsoDateString(nextDayStart)));
    console.log('📅 Fetching previous day shifts:', toIsoDateString(prevDayStart), 'to', dayKey);
    console.log('📅 Fetching previous day shifts:', toIsoDateString(prevDayStart), 'to', dayKey);
    candidates.push(...await fetchDay(toIsoDateString(prevDayStart), dayKey));

    // De-dupe by id
    const byId = new Map();
    for (const s of candidates) {
      if (!s?.id) continue;
      if (!byId.has(s.id)) byId.set(s.id, s);
    }

    const filtered = Array.from(byId.values()).filter(maybeFilter);
    console.log('📦 Total candidates before filter:', byId.size, 'After filter:', filtered.length, filtered.map(s => ({id: s.id, date: s.shiftDateKey, start: s.shiftStartTime})));
    return filtered;
  } catch (err) {
    console.error('findValidShiftsForPunch error', err);
    return [];
  }
}
